import { useState } from 'react';
import { Download, ChevronDown, FileSpreadsheet, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../../api/axios';
import PdfExportModal from './PdfExportModal';
import { buildExportTitle, buildExportFilename } from '../../utils/exportTitle';

const COLUMN_DEFS = [
  { key: 'full_name', label: 'Full Name' },
  { key: 'father_husband_name', label: 'Father/Husband Name' },
  { key: 'gender', label: 'Gender' },
  { key: 'age', label: 'Age' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'caste_category', label: 'Caste Category' },
  { key: 'caste', label: 'Caste' },
  { key: 'profession', label: 'Profession' },
  { key: 'voter_id', label: 'Voter ID' },
  { key: 'phone_number', label: 'Phone Number' },
  { key: 'committee_type', label: 'Committee Type' },
  { key: 'position', label: 'Position' },
  { key: 'affiliated_wing', label: 'Affiliated Wing' },
  { key: 'level', label: 'Level' },
  { key: 'district', label: 'District' },
  { key: 'assembly_name', label: 'Assembly' },
  { key: 'pc', label: 'PC' },
  { key: 'mandal', label: 'Mandal' },
  { key: 'panchayat', label: 'Panchayat' },
  { key: 'status', label: 'Status' },
  { key: 'created_date', label: 'Created Date' },
];

const csvEscape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export default function ExportButton({ filters }) {
  const [open, setOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const doExport = async (format) => {
    setOpen(false);
    try {
      const title = buildExportTitle(filters);
      const generatedAt = new Date().toLocaleString();
      const res = await api.get('/registrations/export', {
        params: { ...filters, format: 'json' },
      });
      const data = Array.isArray(res.data) ? res.data : [];

      if (format === 'csv') {
        const lines = [];
        lines.push(csvEscape(title));
        lines.push(csvEscape(`Generated: ${generatedAt}   |   Records: ${data.length}`));
        lines.push('');
        lines.push(COLUMN_DEFS.map((c) => csvEscape(c.label)).join(','));
        for (const r of data) {
          lines.push(COLUMN_DEFS.map((c) => csvEscape(r[c.key])).join(','));
        }
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildExportFilename(filters, 'csv');
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === 'xlsx') {
        const headerRow = COLUMN_DEFS.map((c) => c.label);
        const bodyRows = data.map((r) => COLUMN_DEFS.map((c) => r[c.key] ?? ''));
        const aoa = [
          [title],
          [`Generated: ${generatedAt}   |   Records: ${data.length}`],
          [],
          headerRow,
          ...bodyRows,
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        // Merge title & meta rows across all columns
        const lastCol = COLUMN_DEFS.length - 1;
        ws['!merges'] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        ];
        // Auto-size columns
        ws['!cols'] = COLUMN_DEFS.map((c, i) => ({
          wch: Math.min(
            40,
            Math.max(c.label.length + 2, ...bodyRows.map((r) => String(r[i] ?? '').length + 2))
          ),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Registrations');
        XLSX.writeFile(wb, buildExportFilename(filters, 'xlsx'));
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-ysrcp-500 text-white rounded-lg hover:bg-ysrcp-600 shadow-sm transition-all duration-200"
      >
        <Download className="w-4 h-4" /> Export <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 bg-white border border-gray-100 rounded-xl shadow-lg z-20 min-w-[200px] overflow-hidden">
            <button onClick={() => doExport('csv')} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-sm hover:bg-ysrcp-50 text-gray-700 hover:text-ysrcp-700 transition-colors">
              <FileSpreadsheet className="w-4 h-4 text-emerald-500" /> Export as CSV
            </button>
            <button onClick={() => doExport('xlsx')} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-sm hover:bg-ysrcp-50 text-gray-700 hover:text-ysrcp-700 transition-colors">
              <FileSpreadsheet className="w-4 h-4 text-green-600" /> Export as Excel
            </button>
            <button onClick={() => { setOpen(false); setPdfOpen(true); }} className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-sm hover:bg-ysrcp-50 text-gray-700 hover:text-ysrcp-700 transition-colors border-t border-gray-50">
              <FileText className="w-4 h-4 text-red-500" /> Export as PDF
            </button>
          </div>
        </>
      )}
      <PdfExportModal open={pdfOpen} onClose={() => setPdfOpen(false)} filters={filters} />
    </div>
  );
}
